import { useState, useRef } from "react";
import { IconButton, Collapse } from "@mui/material";
import {
  getHasSupervisorContext,
  handleEditClick,
  getFormDataToSubmit,
  getAllocationTotalNumMinutes,
} from "./supervisorContextUtils";
import {
  UnknownMinutesAdjustmentStyles,
  EditSubmitFormStyles,
} from "./supervisorContextStyles";
import { ReportButtonStyles } from "../../../../ReportsPage/reportsPageStyles";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { ONE_MINUTE_OF_MS } from "../../../../../utils/timeUtils";
import { round } from "lodash";
import Spinner from "../../../../SimpleComponents/Spinner/Spinner";
import { Button } from "@mui/material";
import SupervisorContextFormContent from "./SupervisorContextFormContent";
import type { AllocationObj } from "types/types";
import { useAllocationListForRange } from "../../../../../utils/customHooks/genericHooks";
import { useFlightTranslation } from "../../../../../utils/customHooks/useFlightTranslation";
import { useMount } from "constants/constants";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { useIsDarkMode } from "store/atoms";

const SupervisorContextForm = ({
  hubletTags,
  date,
  startTime,
  endTime,
  unknownTime,
  timeRange,
  processId,
  scriptUrl,
}) => {
  const [isDarkMode] = useIsDarkMode();

  const allocationListForRange =
    useAllocationListForRange({
      rangeToFind: timeRange,
    }) || [];

  const unknownMinutes =
    round(unknownTime / ONE_MINUTE_OF_MS) +
    getAllocationTotalNumMinutes(allocationListForRange);

  const completedFormStateRef = useRef<AllocationObj[] | any[]>([]);
  const [formState, setFormState] = useState<AllocationObj[] | any[]>([]);

  const numberOfTaggedMinutes = getAllocationTotalNumMinutes(formState);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [isSheetDataLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const { translate } = useFlightTranslation();

  /**
   * When PnlPopup rerenders, this ensures we are getting that popups allocation data
   * and not storing the previously bar's data
   */
  useMount(() => {
    const currentSupervisorContext = allocationListForRange.map(allocObj => {
      return {
        ...allocObj,
        range: timeRange,
        processId,
      };
    });
    setFormState(currentSupervisorContext);
    setIsEditable(currentSupervisorContext.length === 0);
    completedFormStateRef.current = currentSupervisorContext;
  });

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  const getHandleEdit = () => {
    handleEditClick({
      isEditable,
      setIsEditable,
      completedFormStateRef,
      setFormState,
    });
  };

  const handleFormSubmit = e => {
    if (!isSubmitLoading) {
      setIsSubmitLoading(true);
    }
    const formData = getFormDataToSubmit(date, startTime, endTime, formState);

    e.preventDefault();

    fetch(scriptUrl, {
      method: "POST",
      mode: "no-cors",
      body: formData,
    })
      .then(data => {
        setIsSubmitLoading(false);
        if (isEditable) {
          setIsEditable(false);
        }
      })
      .catch(err => {
        console.error("Error:", err);
      });
  };

  const hasSupervisorContext = getHasSupervisorContext(formState);
  const isAboveOrEqualToMinuteLimit = numberOfTaggedMinutes >= unknownMinutes;
  const shouldDisableSubmit =
    !isEditable || isSubmitLoading || !hasSupervisorContext;

  const UnknownMinutesContent = () => (
    <>
      <div className="btnLabel">{translate("Unknown_minutes_adjustments")}</div>
      <div className="alert">
        ({getAllocationTotalNumMinutes(formState)} {translate("of")}{" "}
        {unknownMinutes} min.)
      </div>
    </>
  );

  // don't allow expand if there are 0 unknown minutes to tag, or which have been tagged already
  const shouldAllowExpand = unknownMinutes + numberOfTaggedMinutes > 0;

  if (isSheetDataLoading) {
    return (
      <div style={{ marginTop: "2em" }}>
        <Spinner scale={0.5} />
      </div>
    );
  } else {
    return (
      <UnknownMinutesAdjustmentStyles
        $isAboveOrEqualToMinuteLimit={isAboveOrEqualToMinuteLimit}
        data-server-names={[SERVER_NAMES.supervisorAllocation]}
        data-component-name={"Supervisor Context"}
      >
        <ReportButtonStyles $isDarkMode={isDarkMode}>
          <div className="unknownMinutesLabel">
            {shouldAllowExpand ? (
              <IconButton
                className="btnCollapse"
                onClick={handleExpandClick}
                size="large"
              >
                <ExpandMore
                  fontSize="large"
                  style={{
                    transform: `rotate(${isExpanded ? 0 : -90}deg)`,
                  }}
                />
                <UnknownMinutesContent />
              </IconButton>
            ) : (
              <div
                style={{
                  display: "flex",
                }}
              >
                <UnknownMinutesContent />
              </div>
            )}
          </div>
        </ReportButtonStyles>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit={true}>
          <form onSubmit={handleFormSubmit} method="post">
            <SupervisorContextFormContent
              formState={formState}
              setFormState={setFormState}
              unknownMinutes={unknownMinutes}
              isEditable={isEditable}
              setIsEditable={setIsEditable}
              isAboveOrEqualToMinuteLimit={isAboveOrEqualToMinuteLimit}
              hubletTags={hubletTags}
              timeRange={timeRange}
              processId={processId}
            />
            <EditSubmitFormStyles
              $isSubmitDisabled={shouldDisableSubmit}
              $isEditable={isEditable}
              $isDarkMode={isDarkMode}
            >
              {hasSupervisorContext && (
                <Button
                  variant={isEditable ? undefined : "contained"}
                  type="button"
                  onClick={getHandleEdit}
                >
                  {isEditable ? translate("Cancel") : translate("Edit")}
                </Button>
              )}
              <Button
                disabled={shouldDisableSubmit}
                variant="contained"
                type="submit"
              >
                {!isSubmitLoading ? (
                  translate("Save")
                ) : (
                  <div className="spinnerWrapper">
                    <Spinner scale={0.2} />
                  </div>
                )}
              </Button>
            </EditSubmitFormStyles>
          </form>
        </Collapse>
      </UnknownMinutesAdjustmentStyles>
    );
  }
};

export default SupervisorContextForm;
